import { useQuery, UseQueryResult } from "react-query";
import CustomerApiService from "../../../CustomerApiService";

/**
 * Spotter Usage tab API calls (Monday 12645252035; round 2 = the mockup rebuild). Mirrors
 * `../gamebook/service.ts`'s convention: a thin wrapper over `CustomerApiService.fetch`,
 * bare-JSON contract, plus a named `useQuery` hook (this screen has exactly one query, no params,
 * so one hook is all it needs — see `apps/customer-frontend/CLAUDE.md`'s "Data fetching
 * (react-query)" section).
 *
 * Type mirrors `apps/customer-api/src/services/spotterUsage.ts`'s `SpotterUsageResponse` by hand
 * (the two apps don't share a types package — see that CLAUDE.md section).
 */

export interface SpotterUsageWeeklyPoint {
  /** Monday (UTC) of the ISO week this bar covers, `yyyy-mm-dd`. */
  week_start: string;
  hours: number;
}

export interface SpotterUsageActivityEntry {
  asset_id: string;
  category: "practice" | "game";
  duration_sec: number;
  analyzed_at: string;
  /** Link to the asset's detail page in iconik. */
  asset_url: string;
}

/** GET `/usage`'s response — consumption-only (no cap/%/dollars; see the backend's doc comment
 *  for why). `period_start`/`period_end` are `null` for an org that hasn't been onboarded in
 *  Iconik Manager yet, in which case both meters are also 0 with an empty `weekly_trend`. */
export interface SpotterUsage {
  period_start: string | null;
  period_end: string | null;
  practice: { hours_used: number; weekly_trend: SpotterUsageWeeklyPoint[] };
  game: { hours_used: number; weekly_trend: SpotterUsageWeeklyPoint[] };
  /** Last 6 analyzed assets across both categories, season-to-date, most recent first. */
  recent_activity: SpotterUsageActivityEntry[];
}

export async function getSpotterUsage(): Promise<SpotterUsage> {
  return CustomerApiService.fetch<SpotterUsage>("GET", "/spotter/usage");
}

export const usageQueryKeys = {
  usage: ["spotter-usage"] as const,
};

export function useSpotterUsage(): UseQueryResult<SpotterUsage, Error> {
  return useQuery<SpotterUsage, Error>(usageQueryKeys.usage, getSpotterUsage);
}
